import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import styles from '../../assets/styles/Setting.module.scss';
import ProjectSettingsLayout from '../../components/Setting/ProjectSettingsLayout';
import { useUser } from '../../contexts/UserContext';
import { useProjectContext } from '../../contexts/ProjectContext';
import { getCurrentProject } from '../../utils/localStorage';

// Icons
import iconGa from '../../assets/icons/icon_ga.svg';
import iconGoogleAds from '../../assets/icons/icon-google-ads.svg';
import iconMetaAds from '../../assets/icons/icon-meta-ads.svg';
import iconYahoo from '../../assets/icons/icon-yahoo.svg';

// --- Types ---

type LinkedPlatformsInfo = {
  projectName: string | null;
  gaPropertyId: string | null;
  gaPropertyName: string | null;
  gaId: number | null;
  googleAccountName: string | null;
  googleAccountEmail: string | null;
  googleAccountNameforAds: string | null;
  googleAdAccountName: string | null;
  ga4InitialSyncStatus: InitialSyncStatus;
  googleAdsInitialSyncStatus: InitialSyncStatus;
  ga4SyncLockStatus: InitialSyncStatus;
  googleAdsSyncLockStatus: InitialSyncStatus;
  metaAccountName: string | null;
  metaAdAccountName: string | null;
  metaCampaignList: MetaCampaign[];
  metaSelectedCampaignList: MetaCampaign[];
  metaAdsInitialSyncStatus: InitialSyncStatus;
  metaAdsSyncLockStatus: InitialSyncStatus;
  yaDInitialSyncStatus: InitialSyncStatus;
  yaDSyncLockStatus: InitialSyncStatus;
  yaSInitialSyncStatus: InitialSyncStatus;
  yaSSyncLockStatus: InitialSyncStatus;
  yaDBusinessId: string | null;
  yaDAdAccountName: string | null;
  yaSBusinessId: string | null;
  yaSAdAccountName: string | null;
};

interface ProjectAuth {
  status: string;
  data: LinkedPlatformsInfo;
}

type ExternalData = {
  id: string;
  label: string;
};

type MetaAdAccounts = {
  id: string;
  label: string;
};

type MetaCampaign = {
  id: string;
  label: string;
};

type InitialSyncStatus = 'idle' | 'running' | 'failed';

type YaAdAccounts = {
  id: string;
  label: string;
};

type PlatformAuthStatus = {
  status: string;
  message: string;
  auth_url?: string;
};

type LLMSettingType = {
  status: string;
  message: string;
};

type LLMCurrentSettingType = {
  status: string;
  gpt_organization_id: string | null;
};

const initialLinkedPlatformsInfo: LinkedPlatformsInfo = {
  projectName: null,
  gaPropertyId: null,
  gaPropertyName: null,
  gaId: null,
  googleAccountName: null,
  googleAccountEmail: null,
  googleAccountNameforAds: null,
  googleAdAccountName: null,
  ga4InitialSyncStatus: 'idle',
  googleAdsInitialSyncStatus: 'idle',
  ga4SyncLockStatus: 'idle',
  googleAdsSyncLockStatus: 'idle',
  metaAccountName: null,
  metaAdAccountName: null,
  metaCampaignList: [],
  metaSelectedCampaignList: [],
  metaAdsInitialSyncStatus: 'idle',
  metaAdsSyncLockStatus: 'idle',
  yaDInitialSyncStatus: 'idle',
  yaDSyncLockStatus: 'idle',
  yaSInitialSyncStatus: 'idle',
  yaSSyncLockStatus: 'idle',
  yaDBusinessId: null,
  yaDAdAccountName: null,
  yaSBusinessId: null,
  yaSAdAccountName: null,
};

const areStringArraysEqual = (left: string[], right: string[]) => {
  if (left.length !== right.length) return false;
  const normalizedLeft = [...left].sort();
  const normalizedRight = [...right].sort();
  return normalizedLeft.every((value, index) => value === normalizedRight[index]);
};

// ★ DEMO MODE FLAG ★
// Set to true to verify UI flow without real backend/auth
const USE_DEMO_MODE = false;

const SettingProject: React.FC = () => {
  const { user, fetchUser } = useUser();
  const tenantId = user?.tenant_id;
  const { selectedProjectId, fetchProjects } = useProjectContext();
  const location = useLocation();
  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState(false);
  const [platformInfo, setPlatformInfo] = useState<LinkedPlatformsInfo | null>(initialLinkedPlatformsInfo);

  // --- Google Data ---
  const [gaProperties, setGaProperties] = useState<ExternalData[]>([]);
  const [selectedGaId, setSelectedGaId] = useState<string | null>(null);
  const [selectedGaName, setSelectedGaName] = useState<string | null>(null);

  const [googleAdAccounts, setGoogleAdAccounts] = useState<ExternalData[]>([]);
  const [selectedGoogleAdsId, setSelectedGoogleAdsId] = useState<string | null>(null);
  const [selectedGoogleAdsName, setSelectedGoogleAdsName] = useState<string | null>(null);

  // --- Meta Data ---
  const [metaAccounts, setMetaAccounts] = useState<MetaAdAccounts[]>([]);
  const [selectedMetaId, setSelectedMetaId] = useState<string | null>(null);
  const [selectedMetaName, setSelectedMetaName] = useState<string | null>(null);
  const [metaCampaigns, setMetaCampaigns] = useState<MetaCampaign[]>([]);
  const [selectedMetaCampaignIds, setSelectedMetaCampaignIds] = useState<string[]>([]);

  // --- Yahoo Data ---
  const [yaDisplayAccounts, setYaDisplayAccounts] = useState<YaAdAccounts[]>([]);
  const [selectedYaDisplayId, setSelectedYaDisplayId] = useState<string | null>(null);
  const [selectedYaDisplayName, setSelectedYaDisplayName] = useState<string | null>(null);

  const [yaSearchAccounts, setYaSearchAccounts] = useState<YaAdAccounts[]>([]);
  const [selectedYaSearchId, setSelectedYaSearchId] = useState<string | null>(null);
  const [selectedYaSearchName, setSelectedYaSearchName] = useState<string | null>(null);

  // --- LLM Data ---
  const [gptKey, setGptKey] = useState<string>('');
  const [gptOrg, setGptOrg] = useState<string>('');
  const [initialGptOrg, setInitialGptOrg] = useState<string>('');
  const [message, setMessage] = useState<string>();

  useEffect(() => {
    if (window.location.hash !== '#_=_') return;

    const cleanUrl = `${window.location.pathname}${window.location.search}`;
    window.history.replaceState(null, document.title, cleanUrl);
  }, []);

  const isGoogleAdsInitialSyncRunning = platformInfo?.googleAdsInitialSyncStatus === 'running';
  const isGa4SyncLocked = platformInfo?.ga4SyncLockStatus === 'running';
  const isGoogleAdsSyncLocked = platformInfo?.googleAdsSyncLockStatus === 'running';
  const isMetaAdsSyncLocked = platformInfo?.metaAdsSyncLockStatus === 'running';
  const isYahooDisplaySyncLocked = platformInfo?.yaDSyncLockStatus === 'running';
  const isYahooSearchSyncLocked = platformInfo?.yaSSyncLockStatus === 'running';
  const isAnyPlatformSyncLocked = Boolean(
    isGa4SyncLocked ||
    isGoogleAdsSyncLocked ||
    isMetaAdsSyncLocked ||
    isYahooDisplaySyncLocked ||
    isYahooSearchSyncLocked
  );
  const isGaSelectionChanged = Boolean(
    selectedGaId &&
    selectedGaName &&
    (
      selectedGaId !== platformInfo?.gaPropertyId ||
      selectedGaName !== platformInfo?.gaPropertyName
    )
  );
  const isGoogleAdsSelectionChanged = Boolean(
    selectedGoogleAdsId &&
    selectedGoogleAdsName &&
    selectedGoogleAdsName !== platformInfo?.googleAdAccountName
  );
  const canRetryGoogleAdsInitialSync = platformInfo?.googleAdsInitialSyncStatus === 'failed';
  const isMetaAccountSelectionChanged = Boolean(
    selectedMetaId &&
    selectedMetaName &&
    selectedMetaName !== platformInfo?.metaAdAccountName
  );
  const savedMetaCampaignIds = (platformInfo?.metaSelectedCampaignList || []).map((item) => item.id);
  const isMetaCampaignSelectionChanged = !areStringArraysEqual(
    selectedMetaCampaignIds,
    savedMetaCampaignIds
  );
  const isGoogleAdsMetaCampaignConflict = isGoogleAdsSelectionChanged || isMetaCampaignSelectionChanged;
  const isYahooDisplaySelectionChanged = Boolean(
    selectedYaDisplayId &&
    selectedYaDisplayName &&
    selectedYaDisplayName !== platformInfo?.yaDAdAccountName
  );
  const isYahooSearchSelectionChanged = Boolean(
    selectedYaSearchId &&
    selectedYaSearchName &&
    selectedYaSearchName !== platformInfo?.yaSAdAccountName
  );
  const isLlmSettingChanged = Boolean(
    gptKey || (gptOrg && gptOrg !== initialGptOrg)
  );

  const getGoogleAdsStatusClassName = () => {
    if (isGoogleAdsInitialSyncRunning) return styles.statusPending;
    if (platformInfo?.googleAdAccountName) return styles.statusActive;
    return styles.statusInactive;
  };

  const getGoogleAdsStatusLabel = () => {
    if (isGoogleAdsInitialSyncRunning) return '● 初期連携中';
    if (platformInfo?.googleAdAccountName) return '● 連携済み';
    return '○ 未連携';
  };

  // --- General Fetch Logic ---
  const fetchProjectAuth = async (id: number) => {
    if (USE_DEMO_MODE) return null; // In demo, we rely on local state updates for auth simulation
    try {
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/get-project-auth?project_id=${id}`,
        { credentials: 'include' }
      );
      const data: ProjectAuth = await res.json();
      if (data.status === 'success') return data.data;
      return null;
    } catch (error) {
      console.error('Error fetching project auth:', error);
      return null;
    }
  };

  const fetchPlatformInfo = async () => {
    const project = getCurrentProject();
    if (!project) return;
    const info = await fetchProjectAuth(project.id);
    if (info) setPlatformInfo(info);
  };

  useEffect(() => {
    if (selectedProjectId && tenantId) {
      fetchProjects();
      fetchPlatformInfo();
    }
  }, [tenantId, selectedProjectId]);

  useEffect(() => {
    if (!selectedProjectId || !isAnyPlatformSyncLocked) return;

    const intervalId = window.setInterval(() => {
      fetchPlatformInfo();
    }, 5000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [selectedProjectId, isAnyPlatformSyncLocked]);

  useEffect(() => {
    if (!tenantId) return;
    fetch(`${process.env.REACT_APP_BACKEND_URL}/setting-llm`, {
      credentials: 'include',
    })
      .then((res) => res.json() as Promise<LLMCurrentSettingType>)
      .then((data) => {
        if (data.status === 'success' && data.gpt_organization_id) {
          setGptOrg(data.gpt_organization_id);
          setInitialGptOrg(data.gpt_organization_id);
        }
      })
      .catch((error) => {
        console.error('LLM設定取得エラー:', error);
      });
  }, [tenantId]);

  useEffect(() => {
    if (!platformInfo?.googleAccountName) return;
    fetchGaProperties(false);
  }, [platformInfo?.googleAccountName]);

  useEffect(() => {
    if (!platformInfo?.googleAccountNameforAds && !platformInfo?.googleAdAccountName) return;
    fetchGoogleAdAccounts(false);
  }, [platformInfo?.googleAccountNameforAds, platformInfo?.googleAdAccountName]);

  useEffect(() => {
    if (!platformInfo?.metaAccountName && !platformInfo?.metaAdAccountName) return;
    fetchMetaAccounts(false);
  }, [platformInfo?.metaAccountName, platformInfo?.metaAdAccountName]);

  useEffect(() => {
    if (!platformInfo?.metaAdAccountName) return;
    fetchMetaCampaigns(false);
  }, [platformInfo?.metaAdAccountName]);

  useEffect(() => {
    if (!platformInfo?.yaDBusinessId && !platformInfo?.yaDAdAccountName) return;
    fetchYahooAccounts(false, 'display');
  }, [platformInfo?.yaDBusinessId, platformInfo?.yaDAdAccountName]);

  useEffect(() => {
    if (!platformInfo?.yaSBusinessId && !platformInfo?.yaSAdAccountName) return;
    fetchYahooAccounts(false, 'search');
  }, [platformInfo?.yaSBusinessId, platformInfo?.yaSAdAccountName]);

  useEffect(() => {
    if (!gaProperties.length) return;

    const matched =
      gaProperties.find((item) => item.id === platformInfo?.gaPropertyId) ||
      gaProperties.find((item) => item.label === platformInfo?.gaPropertyName);

    if (!matched) return;
    setSelectedGaId(matched.id);
    setSelectedGaName(matched.label);
  }, [gaProperties, platformInfo?.gaPropertyId, platformInfo?.gaPropertyName]);

  useEffect(() => {
    if (!googleAdAccounts.length) return;

    const matched = googleAdAccounts.find(
      (item) => item.label === platformInfo?.googleAdAccountName
    );

    if (!matched) return;
    setSelectedGoogleAdsId(matched.id);
    setSelectedGoogleAdsName(matched.label);
  }, [googleAdAccounts, platformInfo?.googleAdAccountName]);

  useEffect(() => {
    if (!metaAccounts.length) return;

    const matched = metaAccounts.find(
      (item) => item.label === platformInfo?.metaAdAccountName
    );

    if (!matched) return;
    setSelectedMetaId(matched.id);
    setSelectedMetaName(matched.label);
  }, [metaAccounts, platformInfo?.metaAdAccountName]);

  useEffect(() => {
    if (!yaDisplayAccounts.length) return;

    const matched = yaDisplayAccounts.find(
      (item) => item.label === platformInfo?.yaDAdAccountName
    );

    if (!matched) return;
    setSelectedYaDisplayId(matched.id);
    setSelectedYaDisplayName(matched.label);
  }, [yaDisplayAccounts, platformInfo?.yaDAdAccountName]);

  useEffect(() => {
    if (!yaSearchAccounts.length) return;

    const matched = yaSearchAccounts.find(
      (item) => item.label === platformInfo?.yaSAdAccountName
    );

    if (!matched) return;
    setSelectedYaSearchId(matched.id);
    setSelectedYaSearchName(matched.label);
  }, [yaSearchAccounts, platformInfo?.yaSAdAccountName]);

  useEffect(() => {
    setMetaCampaigns(platformInfo?.metaCampaignList || []);
    setSelectedMetaCampaignIds(
      (platformInfo?.metaSelectedCampaignList || []).map((item) => item.id)
    );
  }, [platformInfo?.metaCampaignList, platformInfo?.metaSelectedCampaignList]);

  // --- Google Handlers (General) ---
  const handleGoogleAuth = (isAdsAuth: boolean) => {
    if (USE_DEMO_MODE) {
      alert('【Demo Mode】Google認証成功をシミュレートします。');
      setPlatformInfo(prev => ({
        ...prev!,
        googleAccountName: 'demo-user@google.com',
        googleAccountNameforAds: isAdsAuth ? 'demo-ads-user@google.com' : prev?.googleAccountNameforAds || null
      }));
      return;
    }

    const project = getCurrentProject();
    if (!project) return;
    setIsLoading(true);
    fetch(`${process.env.REACT_APP_BACKEND_URL}/google-auth`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        is_ads_auth: isAdsAuth,
        tenant_id: tenantId,
        project_id: project.id
      }),
    })
      .then((res) => res.json() as Promise<PlatformAuthStatus>)
      .then((data) => {
        if (data.auth_url) window.location.href = data.auth_url;
        else alert('認証URLの取得に失敗しました: ' + (data.message || 'Unknown error'));
      })
      .catch((error) => {
        console.error('Error fetching Google auth URL:', error);
        alert('認証エラー: APIへの接続に失敗しました');
      })
      .finally(() => setIsLoading(false));
  };

  // GA4
  const fetchGaProperties = async (forceReload: boolean = true) => {
    if (USE_DEMO_MODE) {
      setGaProperties([
        { id: '111', label: 'Demo Property A' },
        { id: '222', label: 'Demo Property B' }
      ]);
      return;
    }

    const project = getCurrentProject();
    if (!project) return;
    setIsLoading(true);
    try {
      const endpoint = forceReload ? 'reload-ga-properties' : 'get-ga-properties';
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/${endpoint}?project_id=${project.id}`,
        { credentials: 'include' }
      );
      const data: ExternalData[] = await res.json();
      setGaProperties(data);
    } catch (err) {
      console.error('GA4 reload error:', err);
      alert('データ取得エラー');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!tenantId || !selectedProjectId) return;
    const params = new URLSearchParams(location.search);
    const authStatus = params.get('status');
    if (!authStatus) return;

    if (authStatus === 'success') {
      fetchPlatformInfo();
      fetchGaProperties(false);
      fetchGoogleAdAccounts(false);
    } else {
      alert('Google認証に失敗しました。時間をおいて再度お試しください。');
    }

    params.delete('status');
    navigate(
      {
        pathname: location.pathname,
        search: params.toString() ? `?${params.toString()}` : '',
      },
      { replace: true }
    );
  }, [location.pathname, location.search, navigate, selectedProjectId, tenantId]);

  const saveGaProperty = async () => {
    const project = getCurrentProject();
    if ((!project && !USE_DEMO_MODE) || !selectedGaId || !selectedGaName) return;

    if (USE_DEMO_MODE) {
      alert('【Demo Mode】保存しました: ' + selectedGaName);
      setPlatformInfo(prev => ({ ...prev!, gaPropertyName: selectedGaName, gaPropertyId: selectedGaId }));
      return;
    }

    setIsLoading(true);
    if (!project) return;
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/save-selected-ga-property`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          project_id: project.id,
          selected_external_data_id: selectedGaId,
          selected_external_data_name: selectedGaName,
        }),
      });
      if (!res.ok) throw new Error();
      alert('保存しました！');
      await fetchPlatformInfo();
    } catch (err) {
      console.error('GA Save Error:', err);
      alert('保存に失敗しました');
    } finally {
      setIsLoading(false);
    }
  };

  // Google Ads
  const fetchGoogleAdAccounts = async (forceReload: boolean = true) => {
    if (USE_DEMO_MODE) {
      setGoogleAdAccounts([
        { id: '333', label: 'Demo Ads Account X' },
        { id: '444', label: 'Demo Ads Account Y' }
      ]);
      return;
    }

    const project = getCurrentProject();
    if (!project) return;
    setIsLoading(true);
    try {
      const endpoint = forceReload ? 'reload-google-ads-accounts' : 'get-google-ad-accounts';
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/${endpoint}?project_id=${project.id}`,
        { credentials: 'include' }
      );
      const data: ExternalData[] = await res.json();
      setGoogleAdAccounts(data);
    } catch (err) {
      console.error('Google Ads reload error:', err);
      alert('データ取得エラー');
    } finally {
      setIsLoading(false);
    }
  };

  const saveGoogleAdAccount = async () => {
    if (USE_DEMO_MODE) {
      alert('【Demo Mode】保存しました: ' + selectedGoogleAdsName);
      setPlatformInfo(prev => ({ ...prev!, googleAdAccountName: selectedGoogleAdsName }));
      return;
    }

    const project = getCurrentProject();
    if (!project || !selectedGoogleAdsId || !selectedGoogleAdsName) return;
    setIsLoading(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/save-selected-google-ad-account`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          project_id: project.id,
          selected_external_data_id: selectedGoogleAdsId,
          selected_external_data_name: selectedGoogleAdsName,
        }),
      });
      if (!res.ok) throw new Error();
      alert('保存しました！');
      await fetchPlatformInfo();
    } catch (err) {
      console.error('Google Ads Save Error:', err);
      alert('保存に失敗しました');
    } finally {
      setIsLoading(false);
    }
  };

  // --- Meta Handlers ---
  const handleMetaAuth = () => {
    if (USE_DEMO_MODE) {
      alert('【Demo Mode】Meta認証成功をシミュレートします。');
      setPlatformInfo(prev => ({ ...prev!, metaAccountName: 'Demo Facebook User' }));
      return;
    }

    const project = getCurrentProject();
    if (!project) return;
    setIsLoading(true);
    fetch(`${process.env.REACT_APP_BACKEND_URL}/meta-auth`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ tenant_id: tenantId, project_id: project.id }),
    })
      .then((res) => res.json() as Promise<PlatformAuthStatus>)
      .then((data) => {
        if (data.auth_url) window.location.href = data.auth_url;
        else alert('認証URL取得失敗: ' + (data.message || 'Unknown'));
      })
      .catch((err) => {
        console.error('Meta auth error:', err);
        alert('認証エラー: API接続失敗');
      })
      .finally(() => setIsLoading(false));
  };

  const fetchMetaAccounts = async (forceReload: boolean = true) => {
    if (USE_DEMO_MODE) {
      setMetaAccounts([
        { id: '555', label: 'Demo Meta Ad Account 1' },
        { id: '666', label: 'Demo Meta Ad Account 2' }
      ]);
      return;
    }

    const project = getCurrentProject();
    if (!project) return;
    setIsLoading(true);
    try {
      const endpoint = forceReload ? 'reload-meta-ad-accounts' : 'get-meta-ad-accounts';
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/${endpoint}?project_id=${project.id}`,
        { credentials: 'include' }
      );
      const data: MetaAdAccounts[] = await res.json();
      setMetaAccounts(data);
    } catch (err) {
      console.error('Meta reload error:', err);
      alert('データ取得エラー');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchMetaCampaigns = async (forceReload: boolean = true) => {
    if (USE_DEMO_MODE) {
      setMetaCampaigns([
        { id: '999', label: 'Demo Meta Campaign 1' },
        { id: '1000', label: 'Demo Meta Campaign 2' },
      ]);
      return;
    }

    const project = getCurrentProject();
    if (!project) return;
    setIsLoading(true);
    try {
      const endpoint = forceReload ? 'reload-meta-campaigns' : 'get-meta-campaigns';
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/${endpoint}?project_id=${project.id}`,
        { credentials: 'include' }
      );
      const data: MetaCampaign[] = await res.json();
      setMetaCampaigns(data);
    } catch (err) {
      console.error('Meta campaign reload error:', err);
      alert('データ取得エラー');
    } finally {
      setIsLoading(false);
    }
  };

  const saveMetaAccount = async () => {
    if (USE_DEMO_MODE) {
      alert('【Demo Mode】保存しました: ' + selectedMetaName);
      setPlatformInfo(prev => ({ ...prev!, metaAdAccountName: selectedMetaName }));
      return;
    }

    const project = getCurrentProject();
    if (!project || !selectedMetaId || !selectedMetaName) return;
    setIsLoading(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/save-selected-meta-ad-account`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          project_id: project.id,
          selected_meta_ad_account_id: selectedMetaId,
          selected_meta_ad_account_name: selectedMetaName,
        }),
      });
      if (!res.ok) throw new Error();
      const campaignRes = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/reload-meta-campaigns?project_id=${project.id}`,
        { credentials: 'include' }
      );
      if (!campaignRes.ok) throw new Error();
      const campaignData: MetaCampaign[] = await campaignRes.json();
      setMetaCampaigns(campaignData);
      setSelectedMetaCampaignIds([]);
      alert('Meta広告アカウントを保存しました！キャンペーン一覧を取得しました。');
      await fetchPlatformInfo();
    } catch (err) {
      console.error('Meta Save Error:', err);
      alert('保存に失敗しました');
    } finally {
      setIsLoading(false);
    }
  };

  const handleMetaCampaignToggle = (campaignId: string) => {
    setSelectedMetaCampaignIds((prev) =>
      prev.includes(campaignId)
        ? prev.filter((id) => id !== campaignId)
        : [...prev, campaignId]
    );
  };

  const saveMetaCampaigns = async () => {
    if (USE_DEMO_MODE) {
      alert('【Demo Mode】Metaキャンペーンを保存しました。');
      return;
    }

    const project = getCurrentProject();
    if (!project) return;

    const selectedMetaCampaignList = metaCampaigns.filter((campaign) =>
      selectedMetaCampaignIds.includes(campaign.id)
    );

    setIsLoading(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/save-selected-meta-campaigns`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          project_id: project.id,
          selected_meta_campaign_list: selectedMetaCampaignList,
        }),
      });
      if (!res.ok) throw new Error();
      alert('Metaキャンペーンを保存しました！');
      await fetchPlatformInfo();
    } catch (err) {
      console.error('Meta Campaign Save Error:', err);
      alert('保存に失敗しました');
    } finally {
      setIsLoading(false);
    }
  };

  // --- Yahoo Handlers ---
  const handleYahooAuth = (adsType: 'display' | 'search') => {
    if (USE_DEMO_MODE) {
      alert('【Demo Mode】Yahoo認証成功をシミュレートします。');
      setPlatformInfo(prev => ({ ...prev!, yaDBusinessId: 'demo-yahoo-biz-id' }));
      return;
    }

    const project = getCurrentProject();
    if (!project) return;
    setIsLoading(true);
    fetch(`${process.env.REACT_APP_BACKEND_URL}/yahoo-auth`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ project_id: project.id, ads_type: adsType }),
    })
      .then((res) => res.json() as Promise<PlatformAuthStatus>)
      .then((data) => {
        if (data.auth_url) window.open(data.auth_url, '_blank');
        else alert('認証URL取得失敗: ' + (data.message || 'Unknown'));
      })
      .catch((err) => {
        console.error('Yahoo auth error:', err);
        alert('認証エラー: API接続失敗');
      })
      .finally(() => setIsLoading(false));
  };

  const fetchYahooAccounts = async (
    forceReload: boolean,
    adsType: 'display' | 'search'
  ) => {
    if (USE_DEMO_MODE) {
      const mockData = [
        { id: '777', label: `Demo Yahoo ${adsType === 'display' ? 'Display' : 'Search'} Account 1` },
        { id: '888', label: `Demo Yahoo ${adsType === 'display' ? 'Display' : 'Search'} Account 2` }
      ];
      if (adsType === 'display') setYaDisplayAccounts(mockData);
      else setYaSearchAccounts(mockData);
      return;
    }

    const project = getCurrentProject();
    if (!project) return;
    setIsLoading(true);
    try {
      const endpoint = forceReload ? 'reload-ya-ad-accounts' : 'get-ya-ad-accounts';
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/${endpoint}?ads_type=${adsType}&project_id=${project.id}`,
        { credentials: 'include' }
      );
      const data: YaAdAccounts[] = await res.json();
      if (adsType === 'display') setYaDisplayAccounts(data);
      else setYaSearchAccounts(data);
    } catch (err) {
      console.error('Yahoo reload error:', err);
      alert('データ取得エラー');
    } finally {
      setIsLoading(false);
    }
  };

  const saveYahooAccount = async (adsType: 'display' | 'search') => {
    const name = adsType === 'display' ? selectedYaDisplayName : selectedYaSearchName;

    if (USE_DEMO_MODE) {
      alert('【Demo Mode】保存しました: ' + name);
      setPlatformInfo(prev => ({
        ...prev!,
        [adsType === 'display' ? 'yaDAdAccountName' : 'yaSAdAccountName']: name
      }));
      return;
    }

    const project = getCurrentProject();
    const id = adsType === 'display' ? selectedYaDisplayId : selectedYaSearchId;

    if (!project || !id || !name) return;
    setIsLoading(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/save-selected-ya-ad-account`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          project_id: project.id,
          ads_type: adsType,
          selected_ya_ad_account_id: id,
        }),
      });
      if (!res.ok) throw new Error();
      alert('Yahoo広告アカウントを保存しました！');
      await fetchPlatformInfo();
    } catch (err) {
      console.error('Yahoo Save Error:', err);
      alert('保存に失敗しました');
    } finally {
      setIsLoading(false);
    }
  };

  // --- LLM Handler ---
  const handleLlmSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!gptKey || !gptOrg) return;
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/setting-llm`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          gpt_api_key: gptKey,
          gpt_organization_id: gptOrg
        }),
      });
      const data: LLMSettingType = await res.json();
      if (data.status === 'success') {
        alert('LLMを登録しました！');
        setMessage(undefined);
        await fetchUser();
      } else {
        alert(`登録に失敗しました...`);
        setMessage(data.message);
      }
    } catch (error) {
      console.error('送信エラー:', error);
    }
  };

  // --- Render Helper ---
  const renderAccountSelector = (
    list: { id: string, label: string }[],
    selectedId: string | null,
    onSelect: (id: string, name: string) => void,
    onSave: () => void,
    title: string,
    isSaveDisabled: boolean = false,
    hasChanges: boolean = false
  ) => (
    <div className={styles.stepBox}>
      <h4>{title}</h4>
      <div className={styles.accountList}>
        {list.map((item) => (
          <div
            key={item.id}
            className={`${styles.accountItem} ${selectedId === item.id ? styles.selected : ''}`}
            onClick={() => onSelect(item.id, item.label)}
          >
            <input
              type="radio"
              checked={selectedId === item.id}
              readOnly
            />
            <span>{item.label}</span>
          </div>
        ))}
      </div>
      <div className={styles.textRight}>
        <button
          className={styles.saveButton}
          disabled={!selectedId || isLoading || isSaveDisabled || !hasChanges}
          onClick={onSave}
        >
          保存する
        </button>
        {isSaveDisabled && (
          <div className={styles.statusPending}>データ連携中のため変更できません</div>
        )}
      </div>
    </div>
  );

  return (
    <ProjectSettingsLayout>
      <div className={styles.twoColumnGrid}>
        <div className={styles.leftColumn}>
          {/* Google Analytics (GA4) */}
          <div className={styles.card}>
            <div className={styles.cardHeader}>
              <div className={styles.headerTitleGroup}>
                <img src={iconGa} alt="GA4" className={styles.platformIcon} />
                <h3>Google Analytics (GA4)</h3>
              </div>
              <div className={platformInfo?.gaPropertyId ? styles.statusActive : styles.statusInactive}>
                {platformInfo?.gaPropertyId ? '● 連携済み' : '○ 未連携'}
              </div>
            </div>
            <div className={styles.formGroup}>
              <p className={styles.descriptionText}>サイト流入データの分析が可能になります。</p>
              <div className={styles.buttonGroup}>
                <button
                  className={styles.actionButton}
                  disabled={isLoading}
                  onClick={() => handleGoogleAuth(false)}
                >
                  {platformInfo?.googleAccountName ? 'Google再連携' : 'Googleでログイン'}
                </button>
                <button
                  className={styles.miniButton}
                  disabled={isLoading}
                  onClick={() => fetchGaProperties(true)}
                >
                  プロパティ一覧を更新
                </button>
              </div>
              {gaProperties.length > 0 && renderAccountSelector(
                gaProperties,
                selectedGaId,
                (id, name) => { setSelectedGaId(id); setSelectedGaName(name); },
                saveGaProperty,
                'GA4プロパティの選択',
                isGa4SyncLocked,
                isGaSelectionChanged
              )}
            </div>
          </div>

          {/* Google Ads */}
          <div className={styles.card}>
            <div className={styles.cardHeader}>
              <div className={styles.headerTitleGroup}>
                <img src={iconGoogleAds} alt="Google Ads" className={styles.platformIcon} />
                <h3>Google Ads 連携</h3>
              </div>
              <div className={getGoogleAdsStatusClassName()}>{getGoogleAdsStatusLabel()}</div>
            </div>
            <div className={styles.formGroup}>
              <p className={styles.descriptionText}>広告データの自動取得や分析が可能になります。</p>
              <div className={styles.buttonGroup}>
                <button
                  className={styles.actionButton}
                  disabled={isLoading}
                  onClick={() => handleGoogleAuth(true)}
                >
                  {platformInfo?.googleAccountNameforAds ? 'Google再連携' : 'Googleでログイン'}
                </button>
                <button
                  className={styles.miniButton}
                  disabled={isLoading}
                  onClick={() => fetchGoogleAdAccounts(true)}
                >
                  アカウント一覧を更新
                </button>
              </div>
              {googleAdAccounts.length > 0 && renderAccountSelector(
                googleAdAccounts,
                selectedGoogleAdsId,
                (id, name) => { setSelectedGoogleAdsId(id); setSelectedGoogleAdsName(name); },
                saveGoogleAdAccount,
                'Google広告アカウントの選択',
                isGoogleAdsSyncLocked || isMetaCampaignSelectionChanged,
                isGoogleAdsSelectionChanged || canRetryGoogleAdsInitialSync
              )}
              {isMetaCampaignSelectionChanged && (
                <div className={styles.statusPending}>Metaキャンペーン未保存のため保存できません</div>
              )}
            </div>
          </div>

          {/* Meta Ads */}
          <div className={styles.card}>
            <div className={styles.cardHeader}>
              <div className={styles.headerTitleGroup}>
                <img src={iconMetaAds} alt="Meta Ads" className={styles.platformIcon} />
                <h3>Meta Ads 連携</h3>
              </div>
              <div className={platformInfo?.metaAdAccountName ? styles.statusActive : styles.statusInactive}>
                {platformInfo?.metaAdAccountName ? '● 連携済み' : '○ 未連携'}
              </div>
            </div>
            <div className={styles.formGroup}>
              <p className={styles.descriptionText}>Facebook/Instagram広告と連携します。</p>
              <div className={styles.buttonGroup}>
                <button
                  className={styles.actionButton}
                  disabled={isLoading}
                  onClick={handleMetaAuth}
                >
                  {platformInfo?.metaAccountName ? 'Facebook再連携' : 'Facebookでログイン'}
                </button>
                <button
                  className={styles.miniButton}
                  disabled={isLoading || !platformInfo?.metaAccountName}
                  onClick={() => fetchMetaAccounts(true)}
                >
                  アカウント一覧を更新
                </button>
              </div>
              {metaAccounts.length > 0 && renderAccountSelector(
                metaAccounts,
                selectedMetaId,
                (id, name) => { setSelectedMetaId(id); setSelectedMetaName(name); },
                saveMetaAccount,
                'Meta広告アカウントの選択',
                isMetaAdsSyncLocked,
                isMetaAccountSelectionChanged
              )}
              {metaCampaigns.length > 0 && (
                <div className={styles.stepBox}>
                  <h4>Metaキャンペーンの選択</h4>
                  <div className={styles.accountList}>
                    {metaCampaigns.map((campaign) => (
                      <label key={campaign.id}>
                        <input
                          type="checkbox"
                          checked={selectedMetaCampaignIds.includes(campaign.id)}
                          onChange={() => handleMetaCampaignToggle(campaign.id)}
                        />
                        <span>{campaign.label}</span>
                      </label>
                    ))}
                  </div>
                  <div className={styles.textRight}>
                    <button
                      className={styles.saveButton}
                      disabled={isLoading || isMetaAdsSyncLocked || isGoogleAdsSelectionChanged || !isMetaCampaignSelectionChanged}
                      onClick={saveMetaCampaigns}
                    >
                      保存する
                    </button>
                    {isMetaAdsSyncLocked && (
                      <div className={styles.statusPending}>データ連携中のため変更できません</div>
                    )}
                    {isGoogleAdsSelectionChanged && (
                      <div className={styles.statusPending}>Google広告アカウント未保存のため保存できません</div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Yahoo Ads (Display) */}
          <div className={styles.card}>
            <div className={styles.cardHeader}>
              <div className={styles.headerTitleGroup}>
                <img src={iconYahoo} alt="Yahoo Ads" className={styles.platformIcon} />
                <h3>Yahoo! ディスプレイ広告</h3>
              </div>
              <div className={isYahooDisplaySyncLocked ? styles.statusPending : platformInfo?.yaDAdAccountName ? styles.statusActive : styles.statusInactive}>
                {isYahooDisplaySyncLocked ? '● 連携中' : platformInfo?.yaDAdAccountName ? '● 連携済み' : '○ 未連携'}
              </div>
            </div>
            <div className={styles.formGroup}>
              <p className={styles.descriptionText}>Yahoo! ディスプレイ広告(YDA)と連携します。</p>
              <div className={styles.buttonGroup}>
                <button
                  className={styles.actionButton}
                  disabled={isLoading}
                  onClick={() => handleYahooAuth('display')}
                >
                  Yahoo! IDでログイン
                </button>
                <button
                  className={styles.miniButton}
                  disabled={isLoading || !platformInfo?.yaDBusinessId}
                  onClick={() => fetchYahooAccounts(true, 'display')}
                >
                  アカウント一覧を更新
                </button>
              </div>
              {yaDisplayAccounts.length > 0 && renderAccountSelector(
                yaDisplayAccounts,
                selectedYaDisplayId,
                (id, name) => { setSelectedYaDisplayId(id); setSelectedYaDisplayName(name); },
                () => saveYahooAccount('display'),
                'ディスプレイ広告アカウントの選択',
                isYahooDisplaySyncLocked,
                isYahooDisplaySelectionChanged
              )}
            </div>
          </div>

          {/* Yahoo Ads (Search) */}
          <div className={styles.card}>
            <div className={styles.cardHeader}>
              <div className={styles.headerTitleGroup}>
                <img src={iconYahoo} alt="Yahoo Ads" className={styles.platformIcon} />
                <h3>Yahoo! 検索広告</h3>
              </div>
              <div className={isYahooSearchSyncLocked ? styles.statusPending : platformInfo?.yaSAdAccountName ? styles.statusActive : styles.statusInactive}>
                {isYahooSearchSyncLocked ? '● 連携中' : platformInfo?.yaSAdAccountName ? '● 連携済み' : '○ 未連携'}
              </div>
            </div>
            <div className={styles.formGroup}>
              <p className={styles.descriptionText}>Yahoo! 検索広告(YSA)と連携します。</p>
              <div className={styles.buttonGroup}>
                <button
                  className={styles.actionButton}
                  disabled={isLoading}
                  onClick={() => handleYahooAuth('search')}
                >
                  Yahoo! IDでログイン
                </button>
                <button
                  className={styles.miniButton}
                  disabled={isLoading || !platformInfo?.yaSBusinessId}
                  onClick={() => fetchYahooAccounts(true, 'search')}
                >
                  アカウント一覧を更新
                </button>
              </div>
              {yaSearchAccounts.length > 0 && renderAccountSelector(
                yaSearchAccounts,
                selectedYaSearchId,
                (id, name) => { setSelectedYaSearchId(id); setSelectedYaSearchName(name); },
                () => saveYahooAccount('search'),
                '検索広告アカウントの選択',
                isYahooSearchSyncLocked,
                isYahooSearchSelectionChanged
              )}
            </div>
          </div>

          {/* Card: AI Integration (Moved from Account) */}
          <div className={styles.card}>
            <div className={styles.cardHeader}>
              <h3>ChatGPT連携設定</h3>
              <div className={`${styles.llmStatus} ${user?.has_gpt_settings ? styles.llmStatusActive : styles.llmStatusInactive}`}>
                {user?.has_gpt_settings ? '● 連携済み' : '○ 未連携'}
              </div>
            </div>

            <form onSubmit={handleLlmSubmit}>
              {message && (
                <div className={styles.alertBox}>
                  {message}
                </div>
              )}

              <div className={styles.formGroup}>
                <label>OpenAI API Key</label>
                <input
                  type="password"
                  value={gptKey}
                  onChange={(e) => setGptKey(e.target.value)}
                  placeholder={user?.has_gpt_settings ? '**********************' : 'sk-...'}
                  required
                  className={styles.inputField}
                />
              </div>

              <div className={styles.formGroup}>
                <label>Organization ID</label>
                <input
                  type="text"
                  value={gptOrg}
                  onChange={(e) => setGptOrg(e.target.value)}
                  placeholder="org-..."
                  required
                  className={styles.inputField}
                />
              </div>

              <div className={styles.textRight}>
                <button
                  type="submit"
                  className={styles.saveButton}
                  disabled={!gptOrg || !isLlmSettingChanged}
                >
                  {user?.has_gpt_settings ? '設定を更新' : '連携する'}
                </button>
              </div>
            </form>
          </div>
        </div>

        {/* Right Column: Current Status */}
        <div className={`${styles.rightColumn} ${styles.rightColumnFixed}`}>
          <div className={styles.card}>
            <div className={styles.cardHeader}>
              <h3>現在の連携状況</h3>
            </div>
            <div className={styles.formGroup}>
              <label>Ga4 アカウント</label>
              <div className={styles.statusValue}>{platformInfo?.googleAccountName || '未連携'}</div>
            </div>
            <div className={styles.formGroup}>
              <label>GA4 プロパティ</label>
              <div className={styles.statusValue}>{platformInfo?.gaPropertyName || '未連携'}</div>
            </div>
            <hr className={styles.separator} />
            <div className={styles.formGroup}>
              <label>GoogleAds ユーザーアカウント</label>
              <div className={styles.statusValue}>{platformInfo?.googleAccountNameforAds || '未連携'}</div>
            </div>
            <div className={styles.formGroup}>
              <label>GoogleAds 広告アカウント</label>
              <div className={styles.statusValue}>{platformInfo?.googleAdAccountName || '未連携'}</div>
              {isGoogleAdsInitialSyncRunning && (
                <div className={styles.statusPending}>初期連携中</div>
              )}
            </div>
            <hr className={styles.separator} />
            <div className={styles.formGroup}>
              <label>Meta アカウント</label>
              <div className={styles.statusValue}>{platformInfo?.metaAccountName || '未連携'}</div>
              <div className={styles.statusValueDisplay}>{platformInfo?.metaAdAccountName}</div>
            </div>
            <hr className={styles.separator} />
            <div className={styles.formGroup}>
              <label>Yahoo! ビジネスID</label>
              <div className={styles.statusValue}>{platformInfo?.yaDBusinessId || platformInfo?.yaSBusinessId || '未連携'}</div>
            </div>
            <div className={styles.formGroup}>
              <label>YDA (ディスプレイ)</label>
              <div className={styles.statusValue}>{platformInfo?.yaDAdAccountName || '未連携'}</div>
            </div>
            <div className={styles.formGroup}>
              <label>YSA (検索)</label>
              <div className={styles.statusValue}>{platformInfo?.yaSAdAccountName || '未連携'}</div>
            </div>
            <hr className={styles.separator} />
            <div className={styles.formGroup}>
              <label>LLM連携</label>
              <div className={styles.statusValue}>
                {user?.has_gpt_settings ? '連携済み' : '未連携'}
              </div>
            </div>
          </div>
        </div>
      </div>
    </ProjectSettingsLayout>
  );
};

export default SettingProject;
